import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { config } from '../../utils/config';
import ClubbieLoader from '../../components/Loader/ClubbieLoader';

const FundraisingList = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: 'All status',
    search: ''
  });

  useEffect(() => {
    fetchCampaigns();
  }, [filters]);

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams();
      if (filters.status !== 'All status') {
        queryParams.append('status', filters.status);
      }
      if (filters.search) {
        queryParams.append('search', filters.search);
      }

      const accessString = localStorage.getItem('access');
      const token = accessString ? JSON.parse(accessString).token : null;

      const response = await fetch(`${config.API_BASE_URL}/${config.API_PATH}/fundraising?${queryParams.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch campaigns');

      const data = await response.json();
      setCampaigns(data.results || []);
    } catch (error) {
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateProgress = (raised, goal) => {
    return Math.min((raised / goal) * 100, 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#000F1F] flex items-center justify-center">
        <ClubbieLoader />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#000F1F] text-[#FEFCEB]">
      <div className="max-w-[1400px] mx-auto py-6 px-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-[#FEFCEB] m-0">Fundraising</h1>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/payments')}
              className="px-5 py-2.5 bg-[#082441] hover:bg-[#0a2d52] border-2 border-[#00E7FF] text-[#00E7FF] rounded-lg font-semibold transition-all flex items-center gap-2"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M10 16l-6-6m0 0l6-6m-6 6h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Back to Payments
            </button>
            <button
              onClick={() => navigate('/fundraising/create')}
              className="bg-[#00E7FF] hover:bg-[#00d4e6] text-[#000F1F] px-6 py-3 rounded-full font-semibold transition-colors"
            >
              + Create campaign
            </button>
          </div>
        </div>

        <div className="flex items-center gap-4 mb-6">
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="bg-[#082441] text-[#FEFCEB] rounded-lg px-4 py-2.5 border border-[#192634] outline-none focus:border-[#00E7FF]"
          >
            <option value="All status">All status</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="draft">Draft</option>
          </select>

          <input
            type="text"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            placeholder="Search campaigns..."
            className="flex-1 bg-[#082441] text-[#FEFCEB] rounded-lg px-4 py-2.5 border border-[#192634] outline-none focus:border-[#00E7FF] placeholder-[#7F8585]"
          />
        </div>

        {campaigns.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-24 h-24 bg-[#082441] rounded-full flex items-center justify-center mb-6">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                <path d="M24 8v32m-16-16h32" stroke="#7F8585" strokeWidth="3" strokeLinecap="round" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-[#FEFCEB] mb-2">No campaigns yet</h2>
            <p className="text-[#7F8585] mb-6">Get started by creating a fundraising campaign</p>
            <button
              onClick={() => navigate('/fundraising/create')}
              className="bg-[#00E7FF] hover:bg-[#00d4e6] text-[#000F1F] px-6 py-3 rounded-full font-semibold transition-colors"
            >
              + Create campaign
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {campaigns.map((campaign) => (
              <div
                key={campaign.id}
                onClick={() => navigate(`/fundraising/${campaign.id}`)}
                className="bg-[#011224] rounded-2xl border border-[#192634] overflow-hidden cursor-pointer hover:border-[#00E7FF] transition-colors"
              >
                {campaign.image && (
                  <div
                    className="h-48 bg-cover bg-center"
                    style={{ backgroundImage: `url(${campaign.image})` }}
                  />
                )}

                <div className="p-6">
                  <h3 className="text-xl font-bold text-[#FEFCEB] mb-2">{campaign.title}</h3>
                  <p className="text-[#7F8585] text-sm mb-4 line-clamp-2">{campaign.subtitle}</p>

                  <div className="mb-4">
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-[#FEFCEB] font-semibold">
                        £{campaign.raised?.toLocaleString() || 0}
                      </span>
                      <span className="text-[#7F8585]">
                        of £{campaign.goal?.toLocaleString() || 0}
                      </span>
                    </div>
                    <div className="h-2 bg-[#192634] rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[#00E7FF] transition-all"
                        style={{ width: `${calculateProgress(campaign.raised || 0, campaign.goal || 1)}%` }}
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <span className="text-[#7F8585]">
                      {campaign.donorsCount || 0} donors
                    </span>
                    {campaign.endDate && (
                      <span className="text-[#7F8585]">
                        Ends {new Date(campaign.endDate).toLocaleDateString('en-GB')}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FundraisingList;
